"use client"; // ob-fix: Ensure this is a client component
import React, { useState } from "react";
import { finishOnboardingCallable, finishOnboardingFallback } from "../../lib/onboarding";
import { GrowthIcon } from '../icons';
import { Loader } from 'lucide-react';

interface GoLiveButtonProps {
  className?: string;
  guidelinesAccepted?: boolean;
}

export default function GoLiveButton({ className = "", guidelinesAccepted = true }: GoLiveButtonProps) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const handleGoLive = async () => {
    // Check if guidelines are accepted
    if (!guidelinesAccepted) {
      alert('Je moet akkoord gaan met de community guidelines om door te gaan.');
      return;
    }
    setErr(null);
    setBusy(true);

    let completed = false;
    try {
      // Try callable first
      await finishOnboardingCallable();
      completed = true;
    } catch (e) {
      try {
        await finishOnboardingFallback();
        completed = true;
      } catch (f) {
        setErr("Afronden mislukt. Probeer opnieuw.");
        setBusy(false);
        return;
      }
    }

    // Ensure local Firestore profile flags are written for route-guard consistency.
    if (completed) {
      try {
        await finishOnboardingFallback();
      } catch {
        // callable may already have succeeded; ignore sync failure
      }
    }

    // Always reset busy state and redirect
    setBusy(false);

    // Queue direct onboarding handoff: open dashboard feed + first-post composer
    try {
      sessionStorage.setItem('pendingOpenNewsfeed', '1');
      sessionStorage.setItem('pendingOpenFirstPostComposer', '1');
      sessionStorage.setItem('onboardingJustCompletedAt', String(Date.now()));
    } catch {
      // ignore storage issues
    }

    // Force window event to update App.tsx state
    window.dispatchEvent(new CustomEvent('onboardingCompleted', { 
      detail: { uid: (window as any).currentUser?.uid } 
    }));

    // Force redirect
    setTimeout(() => {
      // Important: user can be on /onboarding route. Switching only hash keeps pathname
      // on /onboarding, which App route logic can push back to onboarding.
      // Navigate to app root + dashboard hash to guarantee exit from onboarding path.
      window.location.assign('/#dashboard');
      // Also fire runtime events for already-mounted dashboard instances
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('openNewsfeed'));
        window.dispatchEvent(new CustomEvent('openFirstPostComposer'));
      }, 120);
    }, 100);
  };

  return (
    <div className="flex flex-col gap-2">
      <button
        onClick={handleGoLive}
        disabled={busy || !guidelinesAccepted}
        className={`px-8 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-lg font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
      >
        {busy ? (
          <span className="inline-flex items-center gap-2">
            <Loader size={18} className="animate-spin" />
            Bezig...
          </span>
        ) : (
          <span className="inline-flex items-center gap-2"><GrowthIcon size={20} weight="regular" /> Ga Live!</span>
        )}
      </button>
      

      
      {err && (
        <div className="text-red-600 text-sm text-center">
          {err}
        </div>
      )}
    </div>
  );
}
